/**
 * Font loading for the editor.
 *
 * - Google Fonts are loaded on demand, one <link> per family, the first time a family is needed (a row scrolling into view in
 *   the font picker, or a document that uses the family). index.css preloads a handful; everything else used to fall back to
 *   the browser's default serif — which is why most of the font list looked like Times New Roman.
 * - User fonts (TTF / OTF / WOFF / WOFF2 uploaded under Font ▸ My fonts) are stored by the backend and registered here with
 *   the FontFace API, so a Publisher import that names such a font renders with the real thing.
 */
import axios from 'axios';
import { twinOf } from './fontTwins';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Generic families and keywords — never requested from anywhere. Microsoft / Office fonts are NOT in this list any
 *  more: on a computer that has them `isLocalFont` says so; everywhere else their free twin is registered under the
 *  Microsoft name (see fontTwins.js), so an imported Publisher file keeps its letter widths. */
const SYSTEM_FONTS = new Set(['system-ui', 'sans-serif', 'serif', 'monospace', 'cursive', 'fantasy', 'inherit', 'initial']);

// Note: Google still serves legacy family names (Source Sans Pro, Fredoka One, …) from the css2 endpoint, so every family is
// requested under its own name — the @font-face family must match the name used in CSS for the face to apply.

const requested = new Map(); // family (lower) -> Promise<boolean>
const failed = new Set();
const listeners = new Set();
let userFonts = []; // [{ id, family, style, weight, italic, format }]
let userFontsPromise = null;

const notify = () => listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
/** Subscribe to font-set changes (user fonts loaded / added / removed). Returns an unsubscribe function. */
export const onFontsChanged = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

const cleanFamily = (family) => String(family || '').split(',')[0].replace(/["']/g, '').trim();
const isSystem = (family) => SYSTEM_FONTS.has(family.toLowerCase());
const userHas = (family) => userFonts.some((f) => f.family.toLowerCase() === family.toLowerCase());

const localCache = new Map();
/**
 * Is `family` installed on this computer (or already loaded in the page)? Measures a test string with the family in front
 * of two different generic fallbacks: if the width differs from either fallback the family itself is being used. Fonts that
 * came with Office / Windows (Bookman Old Style, Tw Cen MT, Berlin Sans FB, …) are therefore reported as available on a PC
 * that has them, and only as missing where they really are.
 */
export const isLocalFont = (family) => {
  const fam = cleanFamily(family);
  if (!fam) return false;
  const key = fam.toLowerCase();
  if (localCache.has(key)) return localCache.get(key);
  let ok = false;
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const text = 'mmmmmmmmmmlliWQ@0189';
    const width = (font) => { ctx.font = `72px ${font}`; return ctx.measureText(text).width; };
    ok = ['monospace', 'sans-serif', 'serif'].some((fb) => Math.abs(width(`"${fam}", ${fb}`) - width(fb)) > 0.5);
  } catch { ok = false; }
  localCache.set(key, ok);
  return ok;
};

/**
 * Make sure `family` is available: no-op for system / user fonts, otherwise injects a Google Fonts stylesheet
 * (weights 400 + 700, regular + italic where they exist). Resolves true when the stylesheet loaded.
 */
export const ensureFont = (family) => {
  const fam = cleanFamily(family);
  if (!fam || isSystem(fam) || userHas(fam)) return Promise.resolve(true);
  const key = fam.toLowerCase();
  if (requested.has(key)) return requested.get(key);
  if (isLocalFont(fam)) { const done = Promise.resolve(true); requested.set(key, done); return done; }
  const twin = twinOf(fam);
  if (twin) { const p = aliasTwin(twin); requested.set(key, p); return p; }
  const google = fam;
  const p = new Promise((resolve) => {
    if (typeof document === 'undefined') return resolve(false);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(google).replace(/%20/g, '+')}:ital,wght@0,400;0,700;1,400;1,700&display=swap`;
    link.dataset.font = fam;
    link.onload = () => resolve(true);
    link.onerror = () => {
      // Not every family has italics / bold — retry with the plain family before giving up
      const plain = document.createElement('link');
      plain.rel = 'stylesheet';
      plain.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(google).replace(/%20/g, '+')}&display=swap`;
      plain.dataset.font = fam;
      plain.onload = () => resolve(true);
      plain.onerror = () => { failed.add(key); resolve(false); };
      link.remove();
      document.head.appendChild(plain);
    };
    document.head.appendChild(link);
  });
  requested.set(key, p);
  return p;
};

const GOOGLE_CSS = (family) => `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}:ital,wght@0,400;0,700;1,400;1,700&display=swap`;
const GOOGLE_CSS_PLAIN = (family) => `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}&display=swap`;
const aliased = new Set(); // Microsoft names (lower) whose twin is registered

/**
 * Register `twin.twin` under the Microsoft name `twin.name`: fetch the twin's @font-face rules from Google Fonts and rename
 * the family — or, for a twin that ships with the app (`twin.faces`, e.g. Selawik for Segoe UI), write the rules for the
 * bundled files — so `font-family: 'Calibri'` resolves to Carlito wherever Calibri itself is not installed. The injected
 * <style> is a same-origin sheet, so exports (fontEmbed.js) pick the faces up like any other web font.
 */
const localFaceCss = (twin) => twin.faces.map((f) => `@font-face { font-family: '${twin.twin}'; font-style: normal; font-weight: ${f.weight}; font-display: swap; src: url(${f.url}) format('woff2'); }`).join('\n');

const aliasTwin = async (twin) => {
  const key = twin.name.toLowerCase();
  if (aliased.has(key)) return true;
  if (typeof document === 'undefined') return false;
  let css = '';
  if (Array.isArray(twin.faces) && twin.faces.length) css = localFaceCss(twin);
  else {
    for (const url of [GOOGLE_CSS(twin.twin), GOOGLE_CSS_PLAIN(twin.twin)]) {
      try {
        const res = await fetch(url, { mode: 'cors' });
        if (res.ok) { css = await res.text(); break; }
      } catch { /* try the plain request */ }
    }
  }
  if (!css.includes('@font-face')) { failed.add(key); return false; }
  const renamed = css.replace(/font-family:\s*(['"])[^'"]+\1/g, `font-family: '${twin.name.replace(/'/g, "\\'")}'`);
  const style = document.createElement('style');
  style.dataset.fontAlias = twin.name;
  style.dataset.fontTwin = twin.twin;
  style.textContent = `/* ${twin.name} → ${twin.twin}${twin.metric ? ' (metric-compatible)' : ''} */\n${renamed}`;
  document.head.appendChild(style);
  aliased.add(key);
  try { await document.fonts.load(`16px '${twin.name}'`); } catch { /* the faces load lazily anyway */ }
  return true;
};

/** Is this Microsoft family shown through its free twin here (i.e. not installed locally)? */
export const twinInUse = (family) => { const t = twinOf(family); return t && aliased.has(t.name.toLowerCase()) ? t : null; };

/** Load a set of families (deduplicated). */
export const ensureFonts = (families = []) => Promise.all([...new Set(families.map(cleanFamily).filter(Boolean))].map(ensureFont));

/** True when a Google request for this family failed (it is probably not on Google Fonts — upload it under My fonts). */
export const fontFailed = (family) => failed.has(cleanFamily(family).toLowerCase());

/**
 * Where each family comes from: 'system' (installed on this computer), 'user' (uploaded under My fonts), 'google' (loaded
 * from Google Fonts) or 'missing' (nowhere — the browser will substitute a default face). Loads the Google ones on the way.
 */
export const fontStatuses = async (families = []) => {
  await loadUserFonts();
  const fams = [...new Set(families.map(cleanFamily).filter(Boolean))];
  return Promise.all(fams.map(async (family) => {
    if (userHas(family)) return { family, status: 'user' };
    if (isSystem(family) || isLocalFont(family)) return { family, status: 'system' };
    const ok = await ensureFont(family);
    const twin = ok ? twinInUse(family) : null;
    if (twin) return { family, status: 'twin', twin: twin.twin, metric: twin.metric };
    return { family, status: ok ? 'google' : 'missing' };
  }));
};

/**
 * Every font family a publication refers to: object-level `fontFamily`, spans in the objects' HTML content, table cells,
 * WordArt and text on shapes. Used to load the document's fonts when a project opens or a .pub file is imported.
 */
export const collectDocumentFonts = (pages = [], masters = {}, scratch = null) => {
  const out = new Set();
  const visit = (o) => {
    if (!o) return;
    const p = o.properties || {};
    if (p.fontFamily) out.add(cleanFamily(p.fontFamily));
    if (Array.isArray(p.cells)) p.cells.forEach((row) => (Array.isArray(row) ? row : []).forEach((c) => c?.fontFamily && out.add(cleanFamily(c.fontFamily))));
    // rich text (`html`: inline font spans a box mixes), legacy `content`, plain `text`
    const html = [p.html, p.content, p.text].filter((v) => typeof v === 'string').join('\n');
    if (html.includes('font-family') || html.includes('face=')) {
      const re = /font-family:\s*(&quot;|['"])?([^;'">&]+)\1/gi;
      let m;
      while ((m = re.exec(html))) out.add(cleanFamily(m[2]));
      const faceRe = /<font[^>]*\sface=(&quot;|['"])([^'"&>]+)\1/gi;
      while ((m = faceRe.exec(html))) out.add(cleanFamily(m[2]));
    }
    (o.children || []).forEach(visit);
  };
  pages.forEach((pg) => (pg.objects || []).forEach(visit));
  Object.values(masters || {}).forEach((m) => (m.objects || []).forEach(visit));
  (scratch?.objects || []).forEach(visit); // the shared scratch area renders beside every page, so its fonts load too
  out.delete('');
  return [...out];
};

// ---------------------------------------------------------------- user fonts

const fontFileUrl = (f) => `${API}/fonts/user/${f.id}/file`;

const registerUserFont = async (f) => {
  if (typeof FontFace === 'undefined' || !document.fonts) return false;
  try {
    const face = new FontFace(f.family, `url(${fontFileUrl(f)})`, { weight: String(f.weight || 400), style: f.italic ? 'italic' : 'normal' });
    face.__userFontId = f.id;
    await face.load();
    document.fonts.add(face);
    return true;
  } catch (e) {
    console.warn('[fonts] could not load user font', f.family, e?.message || e); // eslint-disable-line no-console
    return false;
  }
};

/** Fetch the user's uploaded fonts once and register them; safe to call repeatedly. */
export const loadUserFonts = (force = false) => {
  if (userFontsPromise && !force) return userFontsPromise;
  userFontsPromise = (async () => {
    try {
      const { data } = await axios.get(`${API}/fonts/user`);
      userFonts = Array.isArray(data) ? data : [];
      await Promise.all(userFonts.map(registerUserFont));
      notify();
      return userFonts;
    } catch (e) {
      console.warn('[fonts] user fonts unavailable', e?.message || e); // eslint-disable-line no-console
      return userFonts;
    }
  })();
  return userFontsPromise;
};

/** Current list of user fonts (summaries). */
export const getUserFonts = () => userFonts;
/** Distinct user font family names, alphabetically. */
export const getUserFontFamilies = () => [...new Set(userFonts.map((f) => f.family))].sort((a, b) => a.localeCompare(b));

/** Upload a font file; the backend reads family / style / weight from the font itself. Returns the new font record. */
export const uploadUserFont = async (file, family) => {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await axios.post(`${API}/fonts/user`, fd, { params: family ? { family } : undefined });
  userFonts = [...userFonts.filter((f) => f.id !== data.id && !(f.family === data.family && f.style === data.style)), data];
  await registerUserFont(data);
  notify();
  return data;
};

/** Remove an uploaded font (and unregister it from the page). */
export const deleteUserFont = async (id) => {
  await axios.delete(`${API}/fonts/user/${id}`);
  userFonts = userFonts.filter((f) => f.id !== id);
  if (document.fonts) {
    [...document.fonts].filter((face) => face.__userFontId === id).forEach((face) => document.fonts.delete(face));
  }
  notify();
};
